
import { useEffect } from 'react';
import { useOrganizationStore } from '@/store/organization.store';
import { useProjectMembers } from '@/hooks/useProjectMembers';
import { profileImageCache } from '@/lib/services/profile-image-cache.service';
import { memberApi } from '@/lib/api/members.api';
import { MemberStatusEnum } from '@scrambl/shared';
import { createLogger } from '@/lib/logger';

const logger = createLogger('components/shared/ProfileImagePreloader');

interface ProfileImagePreloaderProps {
  projectId?: string;
enabled?: boolean;
}

/**
 * Component that preloads profile images for organization/project members
 * Place this high in your component tree to preload images before they're needed
 */
export function ProfileImagePreloader({ 
  projectId, 
  enabled = true 
}: ProfileImagePreloaderProps) {
  const { currentOrganization } = useOrganizationStore();
  const { members: projectMembers } = useProjectMembers(projectId, { enabled: enabled && !!projectId });

  // Global organization member preloading effect
  useEffect(() => {
    if (!enabled || !currentOrganization?.id) {
      return;
    }

    // If no projectId is provided, preload ALL organization members (for app-wide usage)
    if (!projectId) {
      const preloadOrganizationMembers = async () => {
        try {
          // Get all active organization members
          const response = await memberApi.getMembersForDisplay({
            page: 1,
            limit: 200, // Most orgs won't have more than 200 active members
            status: MemberStatusEnum.ACTIVE
          });
          
          const userIds = response.data
            .map(member => member.userId)
            .filter(Boolean);
          
          if (userIds.length > 0) {
            // Preload all organization member profile images in one batch
            await profileImageCache.preloadImages(userIds);
          }
        } catch (error) {
          logger.warn('[ProfileImagePreloader] Failed to preload organization member images:', error);
        }
      };

      preloadOrganizationMembers();
    }
  }, [enabled, currentOrganization?.id, projectId]);

  // Project-specific member preloading effect  
  useEffect(() => {
    if (!enabled || !currentOrganization?.id || !projectId || projectMembers.length === 0) {
      return;
    }

    // Extract user IDs from project members
    const userIds = projectMembers.map(m => m.userId || m.id).filter(Boolean);
    
    if (userIds.length > 0) {
      // Preload project-specific images (likely already cached from org preload above)
      profileImageCache.preloadImages(userIds).catch(error => {
        logger.warn('[ProfileImagePreloader] Failed to preload project member images:', error);
      });
    }
  }, [enabled, currentOrganization?.id, projectMembers, projectId]);

  // This component doesn't render anything
  return null;
}

/**
 * Hook to preload profile images for a list of user IDs
 */
export function usePreloadProfileImages(userIds: string[], enabled = true) {
  useEffect(() => {
    if (!enabled || userIds.length === 0) {
      return;
    }

    // Preload images in the background
    profileImageCache.preloadImages(userIds).catch(error => {
      logger.error('[usePreloadProfileImages] Failed to preload images:', error);
    });
  }, [enabled, userIds]);
}
